/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react';
import { useTranslation } from 'next-i18next';
import {
  CakeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  HomeIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';
import tw from 'twin.macro';
import Image from 'next/image';

import { CarIcon, customImageLoader, GenderIcon, UmbrellaIcon } from '@restworld/utils-common';
import { Paragraph, PresentationCard, Title } from '@restworld/ui-ds';

import {
  birthDateAtom,
  communicationPreferencesAtom,
  displacementAvailabilityAtom,
  displacementPreferenceSummaryAtom,
  drivingLicenceSummaryAtom,
  fullNameAtom,
  transportationOptionsAtom,
  workerMediaAtom,
  workerPersonalDetailsAtom,
  workerPresentationTextAtom,
} from '../../../../employer-job-listing.atoms';
import { TabCardWrapper } from '../candidate-profile-section';

export const MainUserInformation = () => {
  return (
    <div tw="flex flex-col items-start gap-6 lg:pb-4">
      <WorkerBasicSection />
      <MovementSection />
      <MediaSection />
      <PresentationSection />
    </div>
  );
};

const WorkerBasicSection = () => {
  const { t } = useTranslation('onboarding-page');
  const personalDetails = useAtomValue(workerPersonalDetailsAtom);
  const name = useAtomValue(fullNameAtom);
  const birthDate = useAtomValue(birthDateAtom);
  const summaryCommunicationPreference = useAtomValue(communicationPreferencesAtom);
  const errorMessage = t('missing_info');

  return (
    <TabCardWrapper title={t('information_about_the_candidate')}>
      <div id="personal_info" tw="flex flex-col items-start pt-4">
        <div tw="flex lg:flex-row flex-col lg:gap-8 gap-4 w-full">
          <div tw="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
            <PresentationCard label={t('full_name')} header={name || errorMessage} icon={<UserIcon tw="w-6 h-6" />} />
            <PresentationCard label={t('gender')} header={personalDetails?.sex || errorMessage} icon={<GenderIcon />} />
            <PresentationCard
              label={t('birth_date')}
              header={birthDate || errorMessage}
              icon={<CakeIcon tw="w-6 h-6" />}
            />
            <PresentationCard
              label={t('address')}
              header={personalDetails?.address || errorMessage}
              icon={<HomeIcon tw="w-6 h-6" />}
            />
          </div>
          <div tw="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
            <PresentationCard
              label={t('email_address')}
              header={personalDetails?.email || errorMessage}
              icon={<EnvelopeIcon tw="w-6 h-6" />}
            />

            <PresentationCard
              label={t('phone_number')}
              header={personalDetails?.phone_number || errorMessage}
              icon={<PhoneIcon tw="w-6 h-6" />}
            />

            <PresentationCard
              label={t('communication_preferences')}
              header={summaryCommunicationPreference || errorMessage}
              icon={<ChatBubbleOvalLeftEllipsisIcon tw="w-6 h-6" />}
            />
          </div>
        </div>
      </div>
    </TabCardWrapper>
  );
};

const MovementSection = () => {
  const { t } = useTranslation('onboarding-page');

  const drivingLicenceSummary = useAtomValue(drivingLicenceSummaryAtom);
  const transportationOptions = useAtomValue(transportationOptionsAtom);
  const displacementAvailability = useAtomValue(displacementAvailabilityAtom);
  const displacementPreferenceSummary = useAtomValue(displacementPreferenceSummaryAtom);
  const errorMessage = t('missing_info');
  return (
    <TabCardWrapper title={t('movements')}>
      <div tw="">
        <div tw="flex lg:flex-row flex-col lg:gap-8 gap-4 w-full">
          <div tw="flex flex-col items-start  justify-start lg:w-1/2 w-full gap-4">
            <PresentationCard label={t('patents')} header={drivingLicenceSummary || errorMessage} icon={<CarIcon />} />
            <PresentationCard
              label={t('means_of_transportation')}
              header={transportationOptions || errorMessage}
              icon={<CarIcon />}
            />
          </div>
          <div tw="flex flex-col items-start  justify-start lg:w-1/2 w-full gap-4">
            <PresentationCard
              label={t('work_outside_the_city')}
              header={displacementAvailability || errorMessage}
              icon={<UmbrellaIcon />}
            />
            <PresentationCard
              label={t('common:preferences')}
              header={displacementPreferenceSummary || errorMessage}
              icon={<MapPinIcon tw="w-6 h-6" />}
            />
          </div>
        </div>
      </div>
    </TabCardWrapper>
  );
};

export const MediaSection = () => {
  const { t } = useTranslation('onboarding-page');
  const mediaData = useAtomValue(workerMediaAtom);
  return (
    <TabCardWrapper title={t('your_media')}>
      <div id="media" tw="flex flex-col items-start justify-start pt-4">
        {mediaData?.picture ? (
          <Image
            src={mediaData?.picture}
            width={240}
            height={200}
            alt="User profile image"
            tw="rounded-lg"
            loader={customImageLoader}
          />
        ) : (
          <Title fontWeight="normal" textColor="gray-500" variant="h6-large">
            {t('missing_info')}
          </Title>
        )}
      </div>
    </TabCardWrapper>
  );
};

const PresentationSection = () => {
  const { t } = useTranslation('onboarding-page');
  const errorMessage = t('missing_info');
  const presentationData = useAtomValue(workerPresentationTextAtom);

  return (
    <TabCardWrapper title={t('presentation')}>
      <div id="presentation" tw="flex flex-col items-start justify-start pt-6">
        <Title variant="h6-medium" fontWeight="medium" textColor="black" twStyle={tw`mb-2 `}>
          {t('cover_letter')}
        </Title>
        <Paragraph color="gray-500" variant="body" align="left" twStyle={tw`mb-4`}>
          {presentationData?.presentation_text || errorMessage}
        </Paragraph>
        <Title variant="h6-medium" fontWeight="medium" textColor="black" twStyle={tw`mb-2 `}>
          {t('why_are_you_looking_for_work')}
        </Title>
        <Paragraph color="gray-500" variant="body" align="left" twStyle={tw`mb-4`}>
          {presentationData?.motivation_text || errorMessage}
        </Paragraph>
      </div>
    </TabCardWrapper>
  );
};
